'use client';

import { CSSProperties, Fragment, PointerEvent as ReactPointerEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { LegacyBlock, LegacyPageContent, normalizeLegacyContent, pageImageLoader } from '@armorik/page-blocks';
import { buildArticleSlugMap } from '../lib/articleSlugs';
import styles from './PageBlocks.module.css';
import { getFormatLocale } from '../lib/useNationalEnvironment';

const DEFAULT_MORE_INFO = 'More info';
const DEFAULT_FIND_OUT_MORE = 'Find out more';

function safeHref(value?: string) {
  if (!value) return undefined;
  try {
    const url = new URL(value, typeof window === 'undefined' ? 'https://localhost' : window.location.origin);
    return ['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol) ? value : undefined;
  } catch {
    return undefined;
  }
}

function mediaUrl(value?: string, apiBase?: string) {
  const base = apiBase ?? '';
  return value?.startsWith('/api/') ? `${base}${value}` : value;
}

function InlineEdit({ value, multiline, onCommit, onCancel }: { value: string; multiline?: boolean; onCommit: (value: string) => void; onCancel: () => void }) {
  const [draft, setDraft] = useState(value);
  const onBlur = () => onCommit(draft);
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { onCancel(); }
    if (!multiline && e.key === 'Enter') { (e.currentTarget as HTMLElement).blur(); }
    if (multiline && e.ctrlKey && e.key === 'Enter') { (e.currentTarget as HTMLElement).blur(); }
  };
  if (multiline) return <textarea autoFocus value={draft} onChange={e => setDraft(e.target.value)} onBlur={onBlur} onKeyDown={onKey} className="w-full bg-transparent p-0 text-[var(--text)] outline-none resize-none" rows={3} />;
  return <input autoFocus value={draft} onChange={e => setDraft(e.target.value)} onBlur={onBlur} onKeyDown={onKey} className="w-full bg-transparent p-0 text-[var(--text)] outline-none" />;
}

const animationOffset: Record<string, string> = { 'fade-up': 'translateY(20px)', 'fade-left': 'translateX(-20px)', 'scale-in': 'scale(0.95)', 'fade-in': 'none' };

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
}

function formatDateLong(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(getFormatLocale(), { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
}

function extractExcerpt(content: string, maxLen = 120): string {
  if (!content) return '';
  // Strip markdown: headers, bold, italic, links, images, html
  let text = content
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[*_`~]/g, '')
    .replace(/<[^>]+>/g, '')
    .replace(/\n{2,}/g, ' ')
    .replace(/\n/g, ' ')
    .trim();
  // Cut at first sentence boundary if reasonable
  const sentenceEnd = text.search(/[.!?]\s/);
  if (sentenceEnd > 0 && sentenceEnd < maxLen) {
    return text.substring(0, sentenceEnd + 1);
  }
  if (text.length <= maxLen) return text;
  // Cut at last word boundary
  const cut = text.substring(0, maxLen);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > 60 ? cut.substring(0, lastSpace) : cut) + '…';
}

function LinksBlockView({ block, allBlocks, preview }: { block: LegacyBlock; allBlocks?: LegacyBlock[]; preview?: boolean }) {
  const [page, setPage] = useState(0);
  const links = block.links || [];

  // Build date + excerpt + slug lookup from allBlocks
  const dateMap = new Map<string, string>();
  const excerptMap = new Map<string, string>();
  const slugMap = buildArticleSlugMap(allBlocks);
  if (allBlocks) {
    for (const b of allBlocks) {
      if (b.date) dateMap.set(b.id, b.date);
      if (b.content) excerptMap.set(b.id, extractExcerpt(b.content));
    }
  }

  // Check if this block has dated links (aktuality-list)
  const hasDates = links.some(l => {
    const bid = l.url?.split('/').pop() || '';
    return dateMap.has(bid);
  });

  if (!hasDates || preview) {
    // Simple rendering (no dates, or preview mode)
    return <div className={styles.links}>{block.title && <h2>{block.title}</h2>}{links.map((link, index) => { const href = safeHref(link.url); if (!href) return null; const isExternal = href.startsWith('http'); return <a key={`${href}-${index}`} href={href} {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}><span className={styles.linkLabel}>{link.label}</span></a>; })}</div>;
  }

  // Sort links by date descending
  const sortedLinks = [...links].sort((a, b) => {
    const da = dateMap.get(a.url?.split('/').pop() || '') || '';
    const db = dateMap.get(b.url?.split('/').pop() || '') || '';
    return db.localeCompare(da);
  });

  // Group by year
  const grouped: { year: string; links: typeof links }[] = [];
  const yearMap = new Map<string, typeof links>();
  for (const link of sortedLinks) {
    const bid = link.url?.split('/').pop() || '';
    const date = dateMap.get(bid) || '';
    const year = date ? date.substring(0, 4) : '?';
    if (!yearMap.has(year)) yearMap.set(year, []);
    yearMap.get(year)!.push(link);
  }
  for (const [year, yearLinks] of Array.from(yearMap.entries())) {
    grouped.push({ year, links: yearLinks });
  }
  grouped.sort((a, b) => b.year.localeCompare(a.year));

  const PAGE_SIZE = 10;
  const totalLinks = sortedLinks.length;
  const totalPages = Math.ceil(totalLinks / PAGE_SIZE);
  const currentPage = Math.min(page, totalPages - 1);
  const startIdx = currentPage * PAGE_SIZE;
  const endIdx = Math.min(startIdx + PAGE_SIZE, totalLinks);

  // Flatten grouped links with year markers for pagination
  type FlatItem = { type: 'year'; year: string } | { type: 'link'; link: typeof links[0]; date: string; excerpt: string };
  const flat: FlatItem[] = [];
  for (const { year, links: yearLinks } of grouped) {
    flat.push({ type: 'year', year });
    for (const link of yearLinks) {
      const bid = link.url?.split('/').pop() || '';
      flat.push({ type: 'link', link, date: dateMap.get(bid) || '', excerpt: excerptMap.get(bid) || '' });
    }
  }
  const pageItems = flat.slice(startIdx, endIdx);

  return <div className={styles.links}>
    {block.title && <h2>{block.title}</h2>}
    {pageItems.map((item, i) => {
      if (item.type === 'year') {
        return <div key={`year-${item.year}-${i}`} className={styles.yearHeader}>{item.year}</div>;
      }
      const { link, date, excerpt } = item;
      const bid = link.url?.split('/').pop() || '';
      const slug = slugMap.get(bid);
      const slugUrl = slug ? `/nicov/aktuality/${slug}` : link.url;
      const href = safeHref(slugUrl);
      if (!href) return null;
      return <a key={`${href}-${i}`} href={href} className={styles.datedLink}>
        {date && <span className={styles.linkDate}>{formatDate(date)}</span>}
        <span className={styles.linkContent}>
          <span className={styles.linkLabel}>{link.label}</span>
          {excerpt && <span className={styles.linkExcerpt}>{excerpt}</span>}
        </span>
      </a>;
    })}
    {totalPages > 1 && <div className={styles.pagination}>
      <button type="button" className={styles.pageBtn} disabled={currentPage === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>‹ Předchozí</button>
      <span className={styles.pageInfo}>{currentPage + 1} / {totalPages}</span>
      <button type="button" className={styles.pageBtn} disabled={currentPage >= totalPages - 1} onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}>Další ›</button>
    </div>}
  </div>;
}

function BlockView({
  block,
  preview,
  selected,
  onSelect,
  onInlineEdit,
  apiBase,
  moreInfoLabel,
  findOutMoreLabel,
  onGalleryClick,
  allBlocks,
  eventDate
}: {
  block: LegacyBlock;
  preview?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  onInlineEdit?: (patch: Partial<LegacyBlock>) => void;
  apiBase?: string;
  moreInfoLabel?: string;
  findOutMoreLabel?: string;
  onGalleryClick?: (imageIndex: number) => void;
  allBlocks?: LegacyBlock[];
  eventDate?: string;
}) {
  const ref = useRef<HTMLElement>(null);
  const shouldAnimate = !preview && block.animation && block.animation !== 'none';
  const [visible, setVisible] = useState(!shouldAnimate);
  useEffect(() => {
    if (!shouldAnimate) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(entries => { if (entries[0].isIntersecting) setVisible(true); }, { threshold: 0.1 });
    io.observe(el);
    return () => io.disconnect();
  }, [shouldAnimate]);
  const anim = block.animation;
  const animationStyle = shouldAnimate ? { opacity: visible ? 1 : 0, transform: visible ? 'none' : (anim ? animationOffset[anim] : 'none'), transition: 'opacity 0.6s ease, transform 0.6s ease', transitionDelay: `${block.animationDelay || 0}ms` } : {};
  const style = { ...(block.backgroundColor ? { '--block-bg': block.backgroundColor } : {}), ...(block.textColor ? { '--block-color': block.textColor } : {}), ...(block.height ? { minHeight: block.height } : {}), ...animationStyle } as CSSProperties;
  const image = block.imageUrl ? (
    <Image
      src={mediaUrl(block.imageUrl, apiBase) || ''}
      alt={block.imageAlt || ''}
      width={block.width === 'FULL' ? 1920 : 800}
      height={block.width === 'FULL' ? 1080 : 600}
      className={styles.image}
      loader={pageImageLoader}
      unoptimized
      sizes="(max-width: 768px) 100vw, 1200px"
    />
  ) : null;
  const [editing, setEditing] = useState<{ field: string; value: string } | null>(null);
  const commit = (field: string, value: string) => {
    setEditing(null);
    if (!onInlineEdit) return;
    let patch: Partial<LegacyBlock> = {};
    if (field === 'title') patch = { title: value };
    else if (field === 'content') patch = { content: value };
    else if (field === 'linkLabel') patch = { linkLabel: value };
    else if (field.startsWith('links.')) {
      const index = Number(field.split('.')[1]);
      patch = { links: block.links?.map((l, i) => i === index ? { ...l, label: value } : l) };
    }
    onInlineEdit(patch);
  };
  const editable = (field: string, value: string, children: React.ReactNode) => {
    if (!preview || !onInlineEdit) return children;
    if (editing?.field === field) return <InlineEdit value={editing.value} multiline={field === 'content' && (block.type === 'HERO' || block.type === 'MARKDOWN' || block.type === 'CTA')} onCommit={v => commit(field, v)} onCancel={() => setEditing(null)} />;
    return <span onDoubleClick={e => { e.stopPropagation(); setEditing({ field, value }); }} className="cursor-text border-b border-dashed border-current/30 hover:border-current/60">{children}</span>;
  };
  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!onSelect) return;
    const target = e.target as HTMLElement;
    if (target.closest('a') || target.closest('button') || target.isContentEditable) return;
    onSelect();
  };
  const moreLabel = moreInfoLabel ?? DEFAULT_MORE_INFO;
  const findOutLabel = findOutMoreLabel ?? DEFAULT_FIND_OUT_MORE;
  const blockLabel = `${block.type}${block.title ? ` | ${block.title}` : ''}${block.adminLabel ? ` (${block.adminLabel})` : ''}`;
  return <section id={block.id} ref={ref} onClick={handleClick} tabIndex={preview ? 0 : undefined} data-block-label={preview ? blockLabel : undefined} {...(block.type === 'HERO' ? { 'data-nicov-hero': '' } : {})} className={`${styles.block} ${styles[`width${block.width}`]} ${styles[`align${block.alignment}`]} ${preview ? `cursor-pointer transition hover:ring-1 hover:ring-moss/50 ${styles.previewBlock}` : ''} ${selected ? 'ring-2 ring-moss ring-offset-2' : ''}`} style={style}>
    {block.type === 'HERO' && <div className={styles.hero}>{block.imageUrl && <Image src={mediaUrl(block.imageUrl, apiBase) || ''} alt={block.imageAlt || ''} fill className={styles.heroImage} loader={pageImageLoader} unoptimized sizes="100vw" priority />}<div className={styles.heroContent}>{block.title && <h1>{editable('title', block.title, block.title)}</h1>}{eventDate && <h2 className={styles.heroDate}>{eventDate}</h2>}{block.content && editable('content', block.content, <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{block.content}</ReactMarkdown>)}{safeHref(block.linkUrl) && <a className={styles.button} href={safeHref(block.linkUrl)} onDoubleClick={e => { e.preventDefault(); e.stopPropagation(); setEditing({ field: 'linkLabel', value: block.linkLabel || '' }); }}>{block.linkLabel || moreLabel}</a>}</div></div>}
    {block.type === 'MARKDOWN' && <div className={`${styles.markdown} ${block.title === 'Footer' ? styles.footer : ''}`}>{block.title && block.title !== 'Footer' && <div className={styles.markdownHeader}><h3>{editable('title', block.title, block.title)}</h3>{eventDate && <span className={styles.markdownDate}>{formatDateLong(eventDate)}</span>}</div>}{editable('content', block.content || '', <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{block.content || ''}</ReactMarkdown>)}</div>}
    {block.type === 'IMAGE' && (safeHref(block.linkUrl) ? <a href={safeHref(block.linkUrl)} target="_blank" rel="noopener noreferrer">{image}</a> : image)}
    {block.type === 'CTA' && <div className={styles.cta}>{block.title && <h2>{editable('title', block.title, block.title)}</h2>}{block.content && <p>{editable('content', block.content, block.content)}</p>}{safeHref(block.linkUrl) && <a className={styles.button} href={safeHref(block.linkUrl)} {...(safeHref(block.linkUrl)!.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})} onDoubleClick={e => { e.preventDefault(); e.stopPropagation(); setEditing({ field: 'linkLabel', value: block.linkLabel || '' }); }}>{block.linkLabel || findOutLabel}</a>}</div>}
    {block.type === 'LINKS' && <LinksBlockView block={block} allBlocks={allBlocks} preview={preview} />}
    {block.type === 'GALLERY' && <div className={styles.gallery}>{block.title && <h2>{editable('title', block.title, block.title)}</h2>}<div className={styles.galleryThumbs}>{block.images?.filter(item => item.url).map((item, index) => { const href = safeHref(item.link); if (href) return <a key={index} href={href} target="_blank" rel="noopener noreferrer" className={styles.galleryThumb} aria-label={item.alt || `Obrázek ${index + 1}`}><Image src={mediaUrl(item.url, apiBase) || ''} alt={item.alt || ''} width={300} height={220} loader={pageImageLoader} unoptimized sizes="(max-width: 768px) 100vw, 300px" className="w-full h-auto" /></a>; return <button key={index} type="button" className={styles.galleryThumb} onClick={() => onGalleryClick?.(index)} aria-label={item.alt || `Obrázek ${index + 1}`}><Image src={mediaUrl(item.url, apiBase) || ''} alt={item.alt || ''} width={300} height={220} loader={pageImageLoader} unoptimized sizes="(max-width: 768px) 100vw, 300px" className="w-full h-auto" /></button>; })}</div></div>}
    {block.type === 'SPACER' && <div aria-hidden="true" style={{ height: Math.max(8, Math.min(320, block.height || 48)) }} />}
  </section>;
}

function NicovNav({ title, links }: { title?: string; links?: Array<{ label: string; url: string }> }) {
  const heroRef = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);
  const pathname = usePathname();
  const isHome = pathname === '/nicov' || pathname === '/nicov/';
  useEffect(() => {
    if (!isHome) { setVisible(true); return; }
    heroRef.current = document.querySelector(`[data-nicov-hero]`);
    if (!heroRef.current) {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(([entry]) => setVisible(!entry.isIntersecting), { threshold: 0 });
    io.observe(heroRef.current);
    return () => io.disconnect();
  }, [isHome]);
  if (!links?.length) return null;
  return <nav className={`${styles.nav} ${visible ? styles.navVisible : ''}`}>
    <a className={`${styles.navHome} ${isHome ? styles.navLinkActive : ''}`} href="/nicov" aria-label="Nicov — domů"><img src="/nicov-church.png" alt="" className={styles.navHomeIcon} /></a>
    {title && <div className={styles.navTitle}>{title}</div>}
    <div className={styles.navLinks}>
      {links.filter(link => {
        const href = safeHref(link.url);
        if (!href) return false;
        const isExternal = href.startsWith('http');
        return !isExternal && pathname !== href;
      }).map((link, i, filtered) => {
        const href = safeHref(link.url)!;
        return <Fragment key={`${link.url}-${i}`}>{i > 0 && <span className={styles.navSep} />}<a className={styles.navLink} href={href}>{link.label}</a></Fragment>;
      })}
    </div>
  </nav>;
}

function GalleryLightbox({
  blocks,
  startBlock,
  startIndex,
  apiBase,
  onClose
}: {
  blocks: LegacyBlock[];
  startBlock: LegacyBlock;
  startIndex: number;
  apiBase?: string;
  onClose: () => void;
}) {
  const [blockId, setBlockId] = useState(startBlock.id);
  const [imageIndex, setImageIndex] = useState(startIndex);
  const galleries = blocks.filter(b => b.type === 'GALLERY');
  const currentBlock = galleries.find(b => b.id === blockId) || startBlock;
  const currentImages = (currentBlock.images || []).filter(item => item.url);
  const galleryIndex = galleries.findIndex(b => b.id === blockId);

  const prevImage = () => setImageIndex(i => (i - 1 + currentImages.length) % currentImages.length);
  const nextImage = () => setImageIndex(i => (i + 1) % currentImages.length);
  const prevGallery = () => {
    const prev = (galleryIndex - 1 + galleries.length) % galleries.length;
    setBlockId(galleries[prev].id);
    setImageIndex(0);
  };
  const nextGallery = () => {
    const next = (galleryIndex + 1) % galleries.length;
    setBlockId(galleries[next].id);
    setImageIndex(0);
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') { e.preventDefault(); prevImage(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); nextImage(); }
      if (e.key === 'ArrowUp') { e.preventDefault(); prevGallery(); }
      if (e.key === 'ArrowDown') { e.preventDefault(); nextGallery(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [currentImages.length, galleryIndex, galleries.length]);

  const image = currentImages[imageIndex];
  if (!image) return null;
  const href = safeHref(image.link || image.url);

  return <div className={styles.lightbox} onClick={onClose}>
    <div className={styles.lightboxContent} onClick={e => e.stopPropagation()}>
      <button type="button" className={styles.lightboxClose} onClick={onClose} aria-label="Zavřít">×</button>
      <div className={styles.lightboxChapters}>
        {galleries.length > 1 && <button type="button" className={styles.lightboxChapterBtn} onClick={prevGallery}>‹ Předchozí kapitola</button>}
        <div className={styles.lightboxChapter}>{currentBlock.title}</div>
        {galleries.length > 1 && <button type="button" className={styles.lightboxChapterBtn} onClick={nextGallery}>Další kapitola ›</button>}
      </div>
      <div className={styles.lightboxStage}>
        <button type="button" className={styles.lightboxArrow} onClick={prevImage} aria-label="Předchozí">‹</button>
        <a href={href || undefined} target="_blank" rel="noopener noreferrer" className={styles.lightboxImageWrap}>
          <Image src={mediaUrl(image.url, apiBase) || ''} alt={image.alt || ''} width={1200} height={800} loader={pageImageLoader} unoptimized sizes="(max-width: 768px) 100vw, 1200px" className={styles.lightboxImage} />
          {image.alt && <p className={styles.lightboxCaption}>{image.alt}</p>}
          <div className={styles.lightboxCounter}>{imageIndex + 1} / {currentImages.length}</div>
        </a>
        <button type="button" className={styles.lightboxArrow} onClick={nextImage} aria-label="Další">›</button>
      </div>
    </div>
  </div>;
}

export default function PageBlocks({
  content,
  preview = false,
  selectedId,
  onSelectBlock,
  onInlineEdit,
  apiBase,
  moreInfoLabel,
  findOutMoreLabel
}: {
  content: LegacyPageContent;
  preview?: boolean;
  selectedId?: string;
  onSelectBlock?: (id: string) => void;
  onInlineEdit?: (id: string, patch: Partial<LegacyBlock>) => void;
  apiBase?: string;
  moreInfoLabel?: string;
  findOutMoreLabel?: string;
}) {
  const normalized = normalizeLegacyContent(content);
  const theme = normalized.theme || {};
  const search = useSearchParams();
  const query = search ? `?${search.toString()}` : '';
  const style = { '--page-bg': theme.backgroundColor, '--surface': theme.surfaceColor, '--text': theme.textColor, '--accent': theme.accentColor } as CSSProperties;
  const [lightbox, setLightbox] = useState<{ block: LegacyBlock; index: number } | null>(null);
  return <div className={`${styles.baroquePage} ${preview ? styles.preview : ''}`} style={style}>{!preview && <NicovNav title={normalized.title} links={normalized.links} />}<div className={styles.topOrnament} aria-hidden="true"><span>✦</span></div><main className={styles.blockGrid}>{normalized.blocks?.filter(block => block.enabled && (preview || !block.condition || query.includes(block.condition))).map((block, index) => <BlockView key={`${block.id}-${index}`} block={block} preview={preview} selected={selectedId === block.id} onSelect={onSelectBlock ? () => onSelectBlock(block.id) : undefined} onInlineEdit={onInlineEdit ? (patch) => onInlineEdit(block.id, patch) : undefined} apiBase={apiBase} moreInfoLabel={moreInfoLabel} findOutMoreLabel={findOutMoreLabel} onGalleryClick={(imageIndex) => setLightbox({ block, index: imageIndex })} allBlocks={normalized.blocks} eventDate={block.type === 'HERO' ? (normalized.eventDate || normalized.event) : (block.type === 'MARKDOWN' && block.id !== 'nicov-footer' ? normalized.eventDate : undefined)} />)}</main>{lightbox && <GalleryLightbox blocks={normalized.blocks || []} startBlock={lightbox.block} startIndex={lightbox.index} apiBase={apiBase} onClose={() => setLightbox(null)} />}</div>;
}
